import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {OptionsChainService} from '../option-chains.service';
import {isVoid} from '../utils';
import {
    PHA_MAX_PCT_OTM, PHA_MIN_PCT_OTM, PHA_PROTECTION_LIST, PHA_SIDES, PhaExpiration, PhaProtectionRow, PhaProtectionTotals, PhaSide
} from './pha-sheet.model';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';
import {TradingInstrumentsService} from '../trading-instruments/trading-instruments-service.interface';
import {PhaRowMark} from './session/analysis-changes.builder';
import {optionDisplayRows, PhaOptionDisplayRow} from './pha-option-strategy';

// THE PROTECTION TABLE (CR-20260929-001 M4). A custom row grid (ruling R7), dumb by design. The
// instrument is picked from the protection list (SPX / XSP / SPY, ruling R2) or a held name; the
// expiration and the strike from the listed chain; the option is always a put in v1. Dollars are a
// debit (red, negative); "$ Protected" = strike x 100 x contracts, to compare with total beta-delta.

@Component({
    selector: 'ets-pha-protection-table',
    templateUrl: './pha-protection-table.component.html',
    styleUrls: ['./pha-protection-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhaProtectionTableComponent {

    constructor(private readonly _instruments: TradingInstrumentsService, private readonly _chains: OptionsChainService) {
    }

    /** symbol -> its TradingInstrument: the shared strike dropdown's target (owner, 2026-09-29 vrequest). */
    private readonly _instrumentCache: {[symbol: string]: TradingInstrument} = {};

    @Input() rows: PhaProtectionRow[] = [];
    @Input() showGapRows = true;
    @Output() gapPicked = new EventEmitter<{row: PhaProtectionRow, gap: number}>();
    get displayRows(): PhaOptionDisplayRow<PhaProtectionRow>[] { return optionDisplayRows(this.rows, this.showGapRows); }
    @Input() identityRevision = 0;

    readonly trackDisplay = (_: number, entry: PhaOptionDisplayRow<PhaProtectionRow>): string => this.identityRevision + ':' + entry.row.id;
    onGapChanged(row: PhaProtectionRow, ev: {value: number, event?: any}): void {
        if (!ev || !ev.event || !isFinite(ev.value) || ev.value < 0 || this.gapDisabled(row) || ev.value === row.strategyGap) { return; }
        this.gapPicked.emit({row, gap: ev.value});
    }
    gapDisabled(row: PhaProtectionRow): boolean {
        return !this.unlocked || this.rows.some(r => r.strategyId === row.strategyId && (r.crossedOut || this.isExpired(r) || this.isLocked(r)));
    }

    @Input() totals: PhaProtectionTotals = null;

    /**
     * The instruments a row may be on: the list (R2) plus the held names, shown as two groups of the
     * select box (owner, 2026-09-29: "index elements in a bucket of their own").
     */
    @Input()
    set choices(value: Array<{symbol: string, displayName: string}>) {
        const all = value || [];
        const index = all.filter(c => PHA_PROTECTION_LIST.indexOf(c.symbol) >= 0);
        const held = all.filter(c => PHA_PROTECTION_LIST.indexOf(c.symbol) < 0);
        this.groupedChoices = [
            {key: 'Indexes', items: index},
            {key: 'Underlyings', items: held}
        ].filter(g => g.items.length);
    }

    /** DevExtreme grouped items: [{key, items}]. */
    groupedChoices: Array<{key: string, items: Array<{symbol: string, displayName: string}>}> = [];

    @Input() unlocked = false;

    /** The session's marks per row id (CR-20260930-001 M2): changed cells, the lock, the block. A new object on every change. */
    @Input() marks: {[rowId: number]: PhaRowMark} = {};

    /** Copy the order block the row belongs to (the section's option changes are packed together). */
    @Output() copyClicked = new EventEmitter<PhaProtectionRow>();
    /** Revoke the row's copied block (back to not-exported). */
    @Output() revokeClicked = new EventEmitter<PhaProtectionRow>();

    markOf(row: PhaProtectionRow): PhaRowMark | null {
        return (this.marks && this.marks[row.id]) || null;
    }

    isExpired(row: PhaProtectionRow): boolean { return !!row.optionTicker && this._chains.isOptionExpired(row.optionTicker); }

    isLocked(row: PhaProtectionRow): boolean {
        return this.rows.some(r => (r.id === row.id || row.strategyId && r.strategyId === row.strategyId)
            && !!this.markOf(r) && this.markOf(r).locked);
    }

    isOverdue(row: PhaProtectionRow): boolean {
        const m = this.markOf(row);
        return !!m && m.overdue;
    }

    canCopy(row: PhaProtectionRow): boolean {
        const m = this.markOf(row);
        return !this.isExpired(row) && !!m && m.changed && m.status === 'Pending';
    }

    /** The before value of a changed cell, as text; null when the cell is unchanged. */
    beforeOf(row: PhaProtectionRow, cell: 'contracts'): string | null {
        const m = this.markOf(row);
        const v = m && m.before ? m.before[cell] : undefined;
        return v === undefined ? null : v;
    }

    /** Both book holdings and proposed trades are present, even on a single quantity-edited row. */
    get mixedMode(): boolean {
        return !!this.totals && !!this.totals.mixed;
    }

    @Output() symbolPicked = new EventEmitter<{row: PhaProtectionRow, symbol: string}>();
    @Output() expirationPicked = new EventEmitter<{row: PhaProtectionRow, expiration: PhaExpiration | null}>();
    @Output() strikePicked = new EventEmitter<{row: PhaProtectionRow, strike: number | null}>();
    @Output() contractsTyped = new EventEmitter<{row: PhaProtectionRow, contracts: number}>();
    /** The row's own % OTM box (R15): the sheet re-selects the strike to it. */
    @Output() pctOtmPicked = new EventEmitter<{row: PhaProtectionRow, pct: number}>();
    /** The row's OTM lock icon (R18): the sheet flips the flag; the OTM mover skips a locked row. */
    @Output() otmLockToggled = new EventEmitter<PhaProtectionRow>();
    @Output() removeClicked = new EventEmitter<PhaProtectionRow>();
    @Output() restoreClicked = new EventEmitter<PhaProtectionRow>();
    /** The row's SIDE picked from its dropdown (item 4): the sheet flips the sign of its dollars. */
    @Output() sideChanged = new EventEmitter<{row: PhaProtectionRow, side: PhaSide}>();

    readonly centered = {style: 'text-align: center;'};
    readonly minPctOtm = PHA_MIN_PCT_OTM;
    readonly maxPctOtm = PHA_MAX_PCT_OTM;
    readonly sides = PHA_SIDES;

    trackRow(_: number, row: PhaProtectionRow): number {
        return row.id;
    }

    onSideChanged(row: PhaProtectionRow, ev: {value: PhaSide, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || !ev.value || ev.value === row.side) {
            return;
        }
        this.sideChanged.emit({row, side: ev.value});
    }

    /** The instrument as a TradingInstrument, for `ets-strikes-dropdown` (disabled while null). Cached per symbol. */
    instrumentFor(symbol: string): TradingInstrument | null {
        if (!symbol) {
            return null;
        }
        if (!(symbol in this._instrumentCache)) {
            this._instrumentCache[symbol] = this._instruments.getInstrumentByTicker(symbol) || null;
        }
        return this._instrumentCache[symbol];
    }

    /** See PhaPremiumTableComponent.onStrikePicked: drop the dropdown's own echo, emit only a real pick. */
    onStrikePicked(row: PhaProtectionRow, ev: {value: number, previousValue: number}): void {
        if (this.isExpired(row) || isVoid(ev)) {
            return;
        }
        const value = ev.value === null || ev.value === undefined ? null : ev.value;
        if (value === row.strike) {
            return;
        }
        if (value === null && row.strike !== null && row.expiration && row.expiration.strikes.indexOf(row.strike) >= 0) {
            return;
        }
        this.strikePicked.emit({row, strike: value});
    }

    choiceLabel(c: {symbol: string, displayName: string}): string {
        return c && c.displayName && c.displayName !== c.symbol ? `${c.displayName} (${c.symbol})` : (c ? c.symbol : '');
    }

    onSymbolChanged(row: PhaProtectionRow, ev: {value: string, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || !ev.value) {
            return;
        }
        this.symbolPicked.emit({row, symbol: ev.value});
    }

    onExpirationChanged(row: PhaProtectionRow, ev: {value: PhaExpiration, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event)) {
            return;
        }
        this.expirationPicked.emit({row, expiration: ev.value || null});
    }

    onContractsChanged(row: PhaProtectionRow, ev: {value: number, event?: any}): void {
        if (row.crossedOut || this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || ev.value === null || ev.value === undefined || isNaN(ev.value)) {
            return;
        }
        this.contractsTyped.emit({row, contracts: ev.value});
    }

    /** See PhaPremiumTableComponent.onPctOtmChanged: a user edit only, never the re-bind of the actual %. */
    onPctOtmChanged(row: PhaProtectionRow, ev: {value: number, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || ev.value === null || ev.value === undefined || isNaN(ev.value)) {
            return;
        }
        if (ev.value === row.pctOtm) {
            return;
        }
        this.pctOtmPicked.emit({row, pct: ev.value});
    }
}
